// src/App.jsx - FULL WORKING VERSION WITH HR ROUTES
import React from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

// Import your existing pages
import Attendance from "./pages/employee/Attendance";
import EmployeeDashboard from "./pages/employee/Dashboard";
import EmployeeLeave from "./pages/employee/Leave";
import EmployeeProfile from "./pages/employee/Profile";
import EmployeeReports from "./pages/employee/Reports";
import EmployeeTasks from "./pages/employee/Tasks";


import Orders from "./pages/crm/Orders";
import WorkQueue from "./pages/crm/WorkQueue";


// Add with your other page imports
import {
  CRMDashboard,
  Calls,
  ComingSoon,
  Contacts,
  Deals,
  Leads,
  Accounts,
  Reports,
  Meetings,
  Products,
  Quotes,
  Profile,
  Settings,
  CRMLogin
} from './pages/crm';

import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';

// Admin Pages
import {
  Dashboard as AdminDashboard,
  AttendanceReports as AttendancePage,
  ExportCenter,
  GeoLocationLogs as GeoLogsPage,
  WorkReports as ReportsPage,
  SettingsPage,
  UserManagement
} from './pages/admin';

// HR Pages
import {
  AttendanceRecords,
  EmployeeDirectory,
  HRDashboard,
  MyProfile as HRProfile,
  HRReports,
  InternManagement,
  LeaveManagement,
  TakeAttendance
} from './pages/hr';

// Intern Pages
import InternAttendance from './pages/intern/Attendance';
import InternDashboard from './pages/intern/Dashboard';
import InternProfile from './pages/intern/Profile';
import InternReports from './pages/intern/Reports';
import InternTasks from './pages/intern/Tasks';

import TeamAssignment from './pages/admin/TeamAssignment';

// Team Leader Pages
import TeamLeaderDashboard from './pages/team-leader/Dashboard';
import TeamTasks from './pages/team-leader/Tasks';
import TeamAttendance from './pages/team-leader/Attendance';
import TeamLeaves from './pages/team-leader/Leaves';
import TeamReports from './pages/team-leader/Reports';
import TeamMeetings from './pages/team-leader/Meetings';

// Context Providers
import { NotificationProvider } from './context/NotificationContext';
import { ToastProvider } from './context/ToastContext';

// ============ PLACEHOLDER/UTILITY COMPONENTS ============

// Placeholder for non-implemented dashboards
const PlaceholderDashboard = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6', padding: '20px' }}>
      <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', maxWidth: '400px', width: '100%' }}>
        <div style={{ width: '80px', height: '80px', backgroundColor: '#eff6ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '36px' }}>
          ✅
        </div>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Welcome, {user.fullName || user.name || 'User'}!</h1>
        <p style={{ color: '#666', marginBottom: '5px' }}>Role: <strong style={{ color: '#3b82f6' }}>{user.role}</strong></p>
        <p style={{ color: '#999', marginBottom: '25px', fontSize: '14px' }}>Your dashboard is coming soon...</p>
        <button
          onClick={handleLogout}
          style={{ padding: '12px 30px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
        >
          Logout
        </button>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const token = localStorage.getItem('token');
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : {};

  if (!token || !user.role) {
    if (token) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }
    return <Navigate to="/login" replace />;
  }

  // Normalize user role
  const userRole = user.role.trim().toUpperCase();

  if (allowedRoles.length > 0) {
    // Check if user has required permissions
    if (!allowedRoles.includes(userRole)) {
      const dashboards = {
        'ADMIN': '/admin',
        'PARTNER': '/admin',
        'HR': '/hr',
        'MANAGER': '/manager/dashboard',
        'EMPLOYEE': '/employee/dashboard',
        'INTERN': '/intern/dashboard',
        'SALES': '/crm'
      };

      const targetPath = dashboards[userRole] || '/login';
      
      // Prevent infinite redirect if we are already at the target dashboard
      if (window.location.pathname === targetPath || window.location.pathname === `${targetPath}/`) {
        return children;
      }

      console.warn(`[Auth] Access denied for ${userRole} to ${window.location.pathname}. Redirecting to ${targetPath}`);
      return <Navigate to={targetPath} replace />;
    }
  }

  return children;
};

// ============ MAIN APP ============
function App() {
  return (
    <ToastProvider>
      <NotificationProvider>
        <Router>
          <Routes>
            {/* ==================== PUBLIC ROUTES ==================== */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/crm/login" element={<CRMLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/crm/register" element={<Navigate to="/crm/login" replace />} />

            {/* ==================== ADMIN & PARTNER ROUTES ==================== */}
            <Route path="/admin" element={<ProtectedRoute allowedRoles={['ADMIN', 'PARTNER']}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
            <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['ADMIN']}><UserManagement /></ProtectedRoute>} />
            <Route path="/admin/team-assignment" element={<ProtectedRoute allowedRoles={['ADMIN', 'PARTNER']}><TeamAssignment /></ProtectedRoute>} />
            <Route path="/admin/attendance" element={<ProtectedRoute allowedRoles={['ADMIN', 'PARTNER']}><AttendancePage /></ProtectedRoute>} />
            <Route path="/admin/geo-logs" element={<ProtectedRoute allowedRoles={['ADMIN', 'PARTNER']}><GeoLogsPage /></ProtectedRoute>} />
            <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={['ADMIN', 'PARTNER']}><ReportsPage /></ProtectedRoute>} />
            <Route path="/admin/export" element={<ProtectedRoute allowedRoles={['ADMIN', 'PARTNER']}><ExportCenter /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['ADMIN', 'PARTNER']}><SettingsPage /></ProtectedRoute>} />

            {/* ==================== TEAM LEADER ROUTES ==================== */}
            <Route path="/team-leader" element={<ProtectedRoute allowedRoles={['TEAM_LEADER', 'ADMIN']}><TeamLeaderDashboard /></ProtectedRoute>} />
            <Route path="/team-leader/dashboard" element={<Navigate to="/team-leader" replace />} />
            <Route path="/team-leader/tasks" element={<ProtectedRoute allowedRoles={['TEAM_LEADER', 'ADMIN']}><TeamTasks /></ProtectedRoute>} />
            <Route path="/team-leader/attendance" element={<ProtectedRoute allowedRoles={['TEAM_LEADER', 'ADMIN']}><TeamAttendance /></ProtectedRoute>} />
            <Route path="/team-leader/leaves" element={<ProtectedRoute allowedRoles={['TEAM_LEADER', 'ADMIN']}><TeamLeaves /></ProtectedRoute>} />
            <Route path="/team-leader/reports" element={<ProtectedRoute allowedRoles={['TEAM_LEADER', 'ADMIN']}><TeamReports /></ProtectedRoute>} />
            <Route path="/team-leader/meetings" element={<ProtectedRoute allowedRoles={['TEAM_LEADER', 'ADMIN']}><TeamMeetings /></ProtectedRoute>} />

            {/* ==================== HR ROUTES ==================== */}
            <Route path="/hr" element={<ProtectedRoute allowedRoles={['HR', 'ADMIN']}><HRDashboard /></ProtectedRoute>} />
            <Route path="/hr/dashboard" element={<Navigate to="/hr" replace />} />
            <Route path="/hr/take-attendance" element={<ProtectedRoute allowedRoles={['HR', 'ADMIN']}><TakeAttendance /></ProtectedRoute>} />
            <Route path="/hr/attendance" element={<ProtectedRoute allowedRoles={['HR', 'ADMIN']}><AttendanceRecords /></ProtectedRoute>} />
            <Route path="/hr/employees" element={<ProtectedRoute allowedRoles={['HR', 'ADMIN']}><EmployeeDirectory /></ProtectedRoute>} />
            <Route path="/hr/leaves" element={<ProtectedRoute allowedRoles={['HR', 'ADMIN']}><LeaveManagement /></ProtectedRoute>} />
            <Route path="/hr/interns" element={<ProtectedRoute allowedRoles={['HR', 'ADMIN']}><InternManagement /></ProtectedRoute>} />
            <Route path="/hr/reports" element={<ProtectedRoute allowedRoles={['HR', 'ADMIN']}><HRReports /></ProtectedRoute>} />
            <Route path="/hr/profile" element={<ProtectedRoute allowedRoles={['HR', 'ADMIN']}><HRProfile /></ProtectedRoute>} />

            {/* ==================== CRM ROUTES ==================== */}
            {/* <Route path="/crm" element={<ProtectedRoute allowedRoles={['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES']}><CRMDashboard /></ProtectedRoute>} />
            <Route path="/crm/dashboard" element={<Navigate to="/crm" replace />} />
            <Route path="/crm/leads" element={<ProtectedRoute allowedRoles={['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES']}><Leads /></ProtectedRoute>} />
            <Route path="/crm/deals" element={<ProtectedRoute allowedRoles={['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES']}><Deals /></ProtectedRoute>} />
            <Route path="/crm/contacts" element={<ProtectedRoute allowedRoles={['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES']}><Contacts /></ProtectedRoute>} /> */}

            {/* ==================== CRM ROUTES ==================== */}
            <Route path="/crm" element={<ProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES', 'INTERN']}><CRMDashboard /></ProtectedRoute>} />
            <Route path="/crm/dashboard" element={<Navigate to="/crm" replace />} />
            <Route path="/crm/leads" element={
              <ProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES', 'INTERN']}>
                <Leads />
              </ProtectedRoute>
            } />
            <Route path="/crm/deals" element={
              <ProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES']}>
                <Deals />
              </ProtectedRoute>
            } />
            <Route path="/crm/contacts" element={
              <ProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES']}>
                <Contacts />
              </ProtectedRoute>
            } />
            <Route path="/crm/meetings" element={
              <ProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES']}>
                <Meetings />
              </ProtectedRoute>
            } />
            <Route path="/crm/calls" element={
              <ProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES']}>
                <Calls />
              </ProtectedRoute>
            } />
            <Route path="/crm/products" element={
              <ProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'HR', 'MANAGER']}>
                <Products />
              </ProtectedRoute>
            } />
            <Route path="/crm/quotes" element={
              <ProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES']}>
                <Quotes />
              </ProtectedRoute>
            } />
            <Route path="/crm/accounts" element={
              <ProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES']}>
                <Accounts />
              </ProtectedRoute>
            } />
            <Route path="/crm/reports" element={
              <ProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES', 'INTERN']}>
                <Reports />
              </ProtectedRoute>
            } />
            <Route path="/crm/profile" element={
              <ProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES']}>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/crm/settings" element={
              <ProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'HR', 'MANAGER', 'EMPLOYEE', 'SALES']}>
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="/crm/work-queue" element={<WorkQueue />} />
            <Route path="/crm/orders" element={<Orders />} />

            {/* ==================== MANAGER ROUTES ==================== */}
            <Route path="/manager/dashboard" element={<ProtectedRoute allowedRoles={['MANAGER']}><PlaceholderDashboard /></ProtectedRoute>} />

            {/* ==================== INTERN ROUTES ==================== */}
            <Route path="/intern/dashboard" element={<ProtectedRoute allowedRoles={['INTERN']}><InternDashboard /></ProtectedRoute>} />
            <Route path="/intern/attendance" element={<ProtectedRoute allowedRoles={['INTERN']}><InternAttendance /></ProtectedRoute>} />
            <Route path="/intern/profile" element={<ProtectedRoute allowedRoles={['INTERN']}><InternProfile /></ProtectedRoute>} />
            <Route path="/intern/tasks" element={<ProtectedRoute allowedRoles={['INTERN']}><InternTasks /></ProtectedRoute>} />
            <Route path="/intern/reports" element={<ProtectedRoute allowedRoles={['INTERN']}><InternReports /></ProtectedRoute>} />

            {/* ==================== EMPLOYEE ROUTES ==================== */}
            <Route path="/employee/dashboard" element={<ProtectedRoute allowedRoles={['EMPLOYEE']}><EmployeeDashboard /></ProtectedRoute>} />
            <Route path="/employee/attendance" element={<ProtectedRoute allowedRoles={['EMPLOYEE']}><Attendance /></ProtectedRoute>} />
            <Route path="/employee/profile" element={<ProtectedRoute allowedRoles={['EMPLOYEE']}><EmployeeProfile /></ProtectedRoute>} />
            <Route path="/employee/tasks" element={<ProtectedRoute allowedRoles={['EMPLOYEE']}><EmployeeTasks /></ProtectedRoute>} />
            <Route path="/employee/reports" element={<ProtectedRoute allowedRoles={['EMPLOYEE']}><EmployeeReports /></ProtectedRoute>} />
            <Route path="/employee/leave" element={
              <ProtectedRoute allowedRoles={['EMPLOYEE']}>
                <EmployeeLeave />
              </ProtectedRoute>
            } />


            {/* ==================== DEFAULT ROUTES ==================== */}
            <Route path="/" element={
              <ProtectedRoute>
                <Navigate to="/login" replace />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      </NotificationProvider>
    </ToastProvider>
  );
}

export default App;